import { z } from 'zod';

const isAllNullObject = (item: unknown): boolean => {
  if (typeof item !== 'object' || item === null || Array.isArray(item))
    return false;
  const values = Object.values(item);
  return (
    values.length > 0 && values.every((v) => v === null || v === undefined)
  );
};

const unwrapSchema = (schema: z.ZodTypeAny): z.ZodTypeAny => {
  let current = schema;
  while (
    current instanceof z.ZodOptional ||
    current instanceof z.ZodNullable ||
    current instanceof z.ZodDefault
  ) {
    current = current._def.innerType;
  }
  return current;
};

const getUnionVariants = (
  union: z.ZodTypeAny,
): readonly z.ZodTypeAny[] | undefined => {
  if (union instanceof z.ZodDiscriminatedUnion) {
    return union.options as readonly z.ZodTypeAny[];
  }
  if (union instanceof z.ZodUnion) {
    return union.options as readonly z.ZodTypeAny[];
  }
  return undefined;
};

const findVariantForTypename = (
  variants: readonly z.ZodTypeAny[],
  typename: string,
): z.ZodTypeAny | undefined => {
  for (const variant of variants) {
    const inner = unwrapSchema(variant);
    if (!(inner instanceof z.ZodObject)) continue;
    const typenameField = (inner.shape as Record<string, z.ZodTypeAny>)
      .__typename;
    if (
      typenameField instanceof z.ZodLiteral &&
      typenameField.value === typename
    ) {
      return inner;
    }
  }
  return undefined;
};

/**
 * Normalizes returned GraphQL data for UI/form consumption. When a Zod
 * operation schema is provided, recursion is bounded by the schema's
 * structure — we descend through object/array/union nodes and stop at
 * leaves (custom scalars including JSON, enums, primitives). Without this,
 * the recursive null→undefined pass would walk inside JSON scalar payloads
 * (audit log data, settings, webhook payloads) and silently destroy
 * semantically meaningful null values.
 *
 * Without a schema (error-recovery paths where the response shape isn't
 * validated), falls back to a generic recursive normalize.
 */
export const normalizeGraphQLData = <T>(obj: T, schema?: z.ZodTypeAny): T => {
  if (obj === null) return undefined as unknown as T;
  if (obj === undefined) return obj;

  if (schema) {
    const inner = unwrapSchema(schema);

    if (inner instanceof z.ZodObject) {
      if (typeof obj !== 'object' || Array.isArray(obj)) return obj;
      const shape = inner.shape as Record<string, z.ZodTypeAny>;
      return Object.fromEntries(
        Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
          key,
          normalizeGraphQLData(value, shape[key]),
        ]),
      ) as T;
    }

    if (inner instanceof z.ZodArray) {
      if (!Array.isArray(obj)) return obj;
      const elementSchema = inner.element as z.ZodTypeAny;
      return (obj as unknown[])
        .filter((item) => item !== null)
        .map((item) => normalizeGraphQLData(item, elementSchema))
        .filter((item) => !isAllNullObject(item)) as unknown as T;
    }

    const variants = getUnionVariants(inner);
    if (variants) {
      if (typeof obj !== 'object' || Array.isArray(obj)) return obj;
      const typename = (obj as { __typename?: unknown }).__typename;
      const variant =
        typeof typename === 'string'
          ? findVariantForTypename(variants, typename)
          : undefined;
      // No matching variant (unknown __typename, or non-typename-discriminated
      // union) — treat the value as opaque and don't recurse, so we don't
      // mutate fields we can't reason about.
      if (!variant) return obj;
      return normalizeGraphQLData(obj, variant) as T;
    }

    // Leaf: custom scalar (zRequired/JSON), enum, primitive. Return as-is
    // so opaque payloads keep their original null/array semantics.
    return obj;
  }

  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    // Omit null/all-null items while recovering partial GraphQL error
    // payloads (no schema is available to know which slots are required).
    return (obj as unknown[])
      .filter((item) => item !== null)
      .map((item) => normalizeGraphQLData(item))
      .filter((item) => !isAllNullObject(item)) as unknown as T;
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      normalizeGraphQLData(value),
    ]),
  ) as T;
};
